import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { PageHeader, Loading, ErrorState, EmptyState } from "@/components/Bits";
import { Modal, Field, inputCls, primaryBtnCls, ghostBtnCls, Table, Td } from "@/components/Kit";
import { NoAccess } from "@/components/AppShell";
import { PatientForm, PATIENT_EMPTY, validatePatientForm, patientPayload } from "@/components/PatientForm";
import { useAuth } from "@/context/AuthContext";
import { dmy, titleize } from "@/lib/format";
import { firstError } from "@/lib/validate";

export default function Patients() {
  const { can, user } = useAuth();
  const [items, setItems] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [error, setError] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(PATIENT_EMPTY);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState("");
  const [detail, setDetail] = useState(null);
  const [cleanupTarget, setCleanupTarget] = useState(null);
  const [cleanupReason, setCleanupReason] = useState("");
  const [cleanupConfirmed, setCleanupConfirmed] = useState(false);
  const [cleanupError, setCleanupError] = useState("");

  const load = useCallback(async () => {
    setItems(null);
    try {
      const { data } = await api.get("/hospital/patients", { params: { q: q || undefined, page, limit: 25 } });
      setItems(data.items);
      setTotal(data.total);
    } catch (e) { setError(apiError(e)); }
  }, [q, page]);

  useEffect(() => {
    if (!can("hospital.patients.view")) return;
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load, can]);

  if (!can("hospital.patients.view")) return <NoAccess what="patient records" />;

  const canCleanup = can("hospital.records.cleanup")
    && ["business_admin", "hospital_admin"].includes(user?.role);

  const submit = async (e) => {
    e.preventDefault();
    const v = validatePatientForm(form);
    setErrors(v);
    if (Object.keys(v).length) { setFormError(firstError(v)); return; }
    setBusy(true); setFormError("");
    try {
      const { data } = await api.post("/hospital/patients", patientPayload(form));
      toast.success(`Patient registered successfully — UHID ${data.patient.uhid}`);
      setOpen(false); setForm(PATIENT_EMPTY); setErrors({}); load();
    } catch (err) { setFormError(apiError(err)); } finally { setBusy(false); }
  };

  const openDetail = async (p) => {
    try {
      const { data } = await api.get(`/hospital/patients/${p.patient_id}`);
      setDetail(data);
    } catch (e) { toast.error(apiError(e)); }
  };

  const openCleanup = (patient) => {
    setCleanupTarget(patient);
    setCleanupReason("");
    setCleanupConfirmed(false);
    setCleanupError("");
  };

  const submitCleanup = async (event) => {
    event.preventDefault();
    setCleanupError("");
    if (!cleanupConfirmed) {
      setCleanupError("Confirm permanent deletion to continue.");
      return;
    }
    try {
      await api.post(`/hospital/patients/${cleanupTarget.patient_id}/cleanup`, {
        confirmation: true,
        reason: cleanupReason.trim(),
      });
      toast.success("Empty test patient permanently removed and audited.");
      setCleanupTarget(null);
      setDetail(null);
      load();
    } catch (requestError) {
      setCleanupError(apiError(requestError));
    }
  };

  return (
    <div data-testid="patients-page">
      <PageHeader overline="Hospital" title="Patients"
        subtitle="UHIDs are generated per business. Mobile number and government ID are unique within your hospital."
        actions={can("hospital.patients.create") && (
          <button onClick={() => setOpen(true)} className={primaryBtnCls} data-testid="add-patient-btn">
            Register patient
          </button>
        )} />

      <input placeholder="Search name, mobile or UHID" value={q} data-testid="patient-search-input"
        onChange={(e) => { setQ(e.target.value); setPage(1); }}
        className="mb-6 w-full sm:w-96 rounded-lg border border-slate-300 px-4 py-2 text-sm outline-none focus:border-slate-900" />

      {error && <ErrorState message={error} />}
      {!items ? <Loading /> : (
        <>
          <Table testid="patients-table"
            columns={["UHID", "Name", "Gender / Age", "Mobile", "City", "Blood group", "Registered", ""]}
            rows={items}
            empty={<EmptyState title="No patients yet" subtitle="Register your first patient to start OPD and appointments." testid="patients-empty" />}
            renderRow={(p) => (
              <tr key={p.patient_id} data-testid={`patient-row-${p.patient_id}`}>
                <Td className="font-mono-num text-slate-500">{p.uhid}</Td>
                <Td className="font-medium text-slate-900">{p.name}</Td>
                <Td className="text-slate-600 capitalize">{p.gender}{p.age ? ` · ${p.age} yrs` : ""}</Td>
                <Td className="text-slate-600">{p.phone}</Td>
                <Td className="text-slate-600">{p.city || "—"}</Td>
                <Td className="text-slate-600">{p.blood_group || "—"}</Td>
                <Td className="text-slate-500">{dmy(p.created_at)}</Td>
                <Td className="text-right">
                  <button onClick={() => openDetail(p)} className={ghostBtnCls} data-testid={`view-patient-${p.patient_id}`}>
                    View
                  </button>
                </Td>
              </tr>
            )} />
          <div className="mt-5 flex items-center justify-between text-sm text-slate-500">
            <span>{total} patient(s)</span>
            <div className="flex gap-3">
              <button disabled={page === 1} onClick={() => setPage((p) => p - 1)} className={ghostBtnCls} data-testid="patients-prev-btn">Previous</button>
              <button disabled={page * 25 >= total} onClick={() => setPage((p) => p + 1)} className={ghostBtnCls} data-testid="patients-next-btn">Next</button>
            </div>
          </div>
        </>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title="Register patient"
        subtitle="Mandatory fields are marked with *. Records are validated on the server as well."
        testid="add-patient-modal" wide>
        <form onSubmit={submit}>
          <PatientForm form={form} setForm={setForm} errors={errors} prefix="patient" />
          {formError && <p className="mt-6 text-sm font-medium text-red-600" data-testid="patient-form-error">{formError}</p>}
          <div className="mt-7 flex flex-wrap gap-3">
            <button type="submit" disabled={busy} className={primaryBtnCls} data-testid="patient-submit-btn">Register patient</button>
            <button type="button" onClick={() => setOpen(false)} className={ghostBtnCls} data-testid="patient-cancel-btn">Cancel</button>
          </div>
        </form>
      </Modal>

      <Modal open={Boolean(detail)} onClose={() => setDetail(null)} wide
        title={detail?.patient?.name || ""} subtitle={detail ? `UHID ${detail.patient.uhid}` : ""} testid="patient-detail-modal">
        {detail && (
          <div className="space-y-8">
            <div className="grid gap-5 sm:grid-cols-3 text-sm">
              {[["Mobile", detail.patient.phone], ["Gender", detail.patient.gender],
                ["Age", detail.patient.age ?? "—"], ["Blood group", detail.patient.blood_group || "—"],
                ["Government ID", detail.patient.id_type ? `${titleize(detail.patient.id_type)} · ${detail.patient.id_number}` : "—"],
                ["Guardian", detail.patient.guardian_name ? `${detail.patient.guardian_name} (${detail.patient.guardian_relation || "—"})` : "—"],
                ["Address", [detail.patient.area, detail.patient.city, detail.patient.district, detail.patient.state, detail.patient.pincode].filter(Boolean).join(", ") || "—"],
                ["Allergies", detail.patient.allergies || "—"],
                ["Registered", dmy(detail.patient.created_at)]].map(([k, v]) => (
                <div key={k}><p className="overline text-slate-400">{k}</p><p className="mt-1.5">{v}</p></div>
              ))}
            </div>
            {[["Appointments", detail.appointments, (a) => `${dmy(a.scheduled_at)} · Dr. ${a.doctor_name} · ${a.status}`],
              ["OPD visits", detail.opd_visits, (v) => `${dmy(v.created_at)} · Token ${v.token_no} · ${v.status}`],
              ["Admissions", detail.admissions, (a) => `${dmy(a.created_at)} · Bed ${a.bed_number} · ${a.status}`],
              ["Invoices", detail.invoices, (i) => `${i.invoice_no} · ₹ ${i.total} · ${i.status}`]].map(([title, list, fmt]) => (
              <div key={title}>
                <p className="overline text-slate-400 mb-3">{title}</p>
                {list.length === 0 ? <p className="text-sm text-slate-400">No records.</p> : (
                  <ul className="space-y-2 text-sm text-slate-700">
                    {list.map((row, i) => <li key={i} className="rounded-md bg-slate-50 px-4 py-2.5">{fmt(row)}</li>)}
                  </ul>
                )}
              </div>
            ))}
            {canCleanup && (
              <div className="border-t border-slate-200 pt-5">
                <button onClick={() => openCleanup(detail.patient)}
                  className="text-sm font-medium text-red-600 hover:text-red-800"
                  data-testid={`cleanup-patient-${detail.patient.patient_id}`}>
                  Remove empty test record
                </button>
              </div>
            )}
          </div>
        )}
      </Modal>

      <Modal open={Boolean(cleanupTarget)} onClose={() => setCleanupTarget(null)}
        title="Remove empty test patient"
        subtitle={cleanupTarget ? `${cleanupTarget.name} · UHID ${cleanupTarget.uhid}` : ""}
        testid="patient-cleanup-modal">
        <form onSubmit={submitCleanup} className="space-y-5">
          <p className="text-sm text-slate-700" data-testid="patient-cleanup-warning">
            This record contains no financial or clinical transactions and will be permanently deleted.
          </p>
          <Field label="Deletion reason *">
            <textarea required minLength={3} rows={3} className={inputCls}
              data-testid="patient-cleanup-reason" value={cleanupReason}
              onChange={(event) => setCleanupReason(event.target.value)} />
          </Field>
          <label className="flex items-start gap-2 text-sm text-slate-700"
            data-testid="patient-cleanup-confirmation">
            <input type="checkbox" checked={cleanupConfirmed}
              onChange={(event) => setCleanupConfirmed(event.target.checked)} />
            I understand this cannot be undone.
          </label>
          {cleanupError && <p className="text-sm font-medium text-red-600" data-testid="patient-cleanup-error">{cleanupError}</p>}
          <button className={primaryBtnCls} data-testid="patient-cleanup-submit">Permanently delete</button>
        </form>
      </Modal>
    </div>
  );
}
